import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";

function CollectionCard({ col, onDelete }) {
  const [copied, setCopied] = useState(false);

  const copyLink = (e) => {
    e.preventDefault();
    navigator.clipboard.writeText(`${window.location.origin}/collections/${col._id}`);
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };

  const covers = col.books.slice(0, 4);
  const extra  = col.books.length - 4;

  return (
    <Link to={`/collections/${col._id}`}
      className="group flex flex-col bg-ink-surface border border-ink-border rounded-xl overflow-hidden hover:border-ink-muted transition-colors">
      {/* Cover grid */}
      <div className="grid grid-cols-2 aspect-[2/1] overflow-hidden">
        {covers.length === 0 && (
          <div className="col-span-2 row-span-2 bg-ink-raised flex items-center justify-center">
            <span className="text-cream-muted text-xs">Empty</span>
          </div>
        )}
        {covers.map((b, i) => (
          <div key={i} className="relative overflow-hidden bg-ink-raised">
            {b.imageLink
              ? <img src={b.imageLink} alt={b.title} className="w-full h-full object-cover" />
              : <div className="w-full h-full flex items-center justify-center">
                  <span className="text-cream-muted text-xs font-bold">{(b.title || '?')[0]}</span>
                </div>
            }
            {i === 3 && extra > 0 && (
              <div className="absolute inset-0 bg-ink-base/70 flex items-center justify-center">
                <span className="text-cream text-sm font-bold">+{extra}</span>
              </div>
            )}
          </div>
        ))}
        {covers.length === 1 && <div className="bg-ink-raised" />}
        {covers.length === 2 && <><div className="bg-ink-raised" /><div className="bg-ink-raised" /></>}
        {covers.length === 3 && <div className="bg-ink-raised" />}
      </div>

      {/* Info */}
      <div className="p-4 flex flex-col gap-1 flex-1">
        <p className="text-cream text-sm font-semibold line-clamp-1">{col.title}</p>
        {col.description && (
          <p className="text-cream-muted text-xs line-clamp-2">{col.description}</p>
        )}
        <p className="text-cream-muted text-[11px] mt-auto pt-2">
          {col.books.length} book{col.books.length !== 1 ? 's' : ''}
        </p>
      </div>

      {/* Footer actions */}
      <div className="px-4 pb-3 flex items-center gap-3" onClick={e => e.preventDefault()}>
        <button onClick={copyLink}
          className="text-[11px] text-cream-muted hover:text-cream transition-colors">
          {copied ? 'Copied!' : 'Copy link'}
        </button>
        <button onClick={e => { e.preventDefault(); onDelete(col); }}
          className="text-[11px] text-cream-muted hover:text-crimson transition-colors ml-auto">
          Delete
        </button>
      </div>
    </Link>
  );
}

function Collections() {
  const navigate = useNavigate();
  const userId = localStorage.getItem("userId");

  const [cols,    setCols]    = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [form,    setForm]    = useState({ title: '', description: '' });
  const [saving,  setSaving]  = useState(false);

  useEffect(() => {
    if (!userId) { navigate('/login'); return; }
    fetchCols();
  }, [userId, navigate]); // eslint-disable-line

  const fetchCols = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`/api/collections/user?userId=${encodeURIComponent(userId)}`);
      setCols(data.collections || []);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) return;
    setSaving(true);
    try {
      const { data } = await axios.post('/api/collections/create', { userId, ...form });
      setCols(prev => [data.collection, ...prev]);
      setForm({ title: '', description: '' });
      setCreating(false);
    } finally { setSaving(false); }
  };

  const handleDelete = async (col) => {
    if (!window.confirm(`Delete "${col.title}"?`)) return;
    await axios.post('/api/collections/delete', { id: col._id, userId });
    setCols(prev => prev.filter(c => c._id !== col._id));
  };

  return (
    <div className="min-h-screen bg-ink-base">
      <div className="max-w-4xl mx-auto px-5 sm:px-8 py-10">

        {/* Header */}
        <div className="flex items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-2xl font-bold text-cream tracking-tight">Collections</h1>
            <p className="text-cream-muted text-sm mt-0.5">
              {loading ? 'Loading…' : `${cols.length} collection${cols.length !== 1 ? 's' : ''}`}
            </p>
          </div>
          <button onClick={() => setCreating(o => !o)}
            className="sc-btn-gold text-xs py-1.5 px-4 flex-shrink-0">
            {creating ? 'Cancel' : '+ New collection'}
          </button>
        </div>

        {/* Create form */}
        {creating && (
          <form onSubmit={handleCreate}
            className="bg-ink-surface border border-ink-border rounded-xl p-5 space-y-4 mb-8">
            <div>
              <label className="text-cream-muted text-xs font-medium block mb-1.5">Title</label>
              <input autoFocus className="sc-input text-sm" value={form.title}
                onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                placeholder="e.g. Books that changed me" required />
            </div>
            <div>
              <label className="text-cream-muted text-xs font-medium block mb-1.5">
                Description <span className="text-cream-muted/60 font-normal">— optional</span>
              </label>
              <textarea className="sc-input resize-none text-sm" rows={2} value={form.description}
                onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                placeholder="What's this list about?" />
            </div>
            <div className="flex gap-2">
              <button type="submit" disabled={saving}
                className="sc-btn-gold text-xs py-1.5 px-4 disabled:opacity-50">
                {saving ? 'Creating…' : 'Create'}
              </button>
              <button type="button" onClick={() => setCreating(false)} className="sc-btn-ghost text-xs py-1.5 px-4">
                Cancel
              </button>
            </div>
          </form>
        )}

        {/* Grid */}
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
            {[1, 2, 3].map(i => (
              <div key={i} className="bg-ink-surface border border-ink-border rounded-xl h-52 animate-pulse" />
            ))}
          </div>
        ) : cols.length === 0 ? (
          <div className="py-24 text-center">
            <p className="text-cream font-semibold mb-1">No collections yet</p>
            <p className="text-cream-muted text-sm mb-6 max-w-xs mx-auto">
              Create a list to curate and share books with anyone.
            </p>
            <button onClick={() => setCreating(true)} className="sc-btn-gold text-sm">
              Create your first collection
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
            {cols.map(col => (
              <CollectionCard key={col._id} col={col} onDelete={handleDelete} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default Collections;
